/**
 * @fileoverview The guided-tour script for the report builder canvas (mirrors the
 *             document builder's builderTourSteps).
 * @module ui-admin/components/ReportCanvasBuilder/reportTourSteps
 *
 * Responsibilities: - Define the ordered TourSteps that walk a first-time user
 *                     through the report builder — canvas surfaces, field palette,
 *                     column cards, parameters, preview, save/publish.
 * Dependencies:     - TourStep from the help types.
 * Architecture:     - Pure static data bundled with the app (no API/Dexie, no mock
 *                     data). Co-located with the shared ReportCanvasBuilder so both
 *                     tenant-web and system-web reuse the same tour; targets that
 *                     live on the host page carry matching data-tour attributes.
 * Notes:            - GuidedTour centres the tooltip when a targetSelector is
 *                     empty or missing, so the tour runs gracefully even if a host
 *                     omits an optional target.
 */

import type { TourStep } from '../../help/types';

/** Stable `data-tour` attribute values used as the tour's CSS selector targets. */
export const REPORT_TOUR_TARGET = {
  leftPanel: 'left-panel',
  surfaceTabs: 'surface-tabs',
  canvas: 'canvas',
  addField: 'add-field',
  columnCard: 'column-card',
  columnMenu: 'column-menu',
  parameters: 'parameters-accordion',
  schedule: 'schedule-accordion',
  preview: 'preview',
  previewHandle: 'preview-handle',
  savePublish: 'save-publish',
} as const;

const sel = (target: string) => `[data-tour="${target}"]`;

/**
 * @contract REPORT_TOUR_STEPS
 * Purpose: Ordered walkthrough of the report builder canvas (per spec §7).
 * Rules: Step order is the tour order; every non-welcome step targets a real
 *   element selector (host pages provide matching data-tour attributes).
 */
export const REPORT_TOUR_STEPS: TourStep[] = [
  {
    targetSelector: '',
    title: 'Welcome to the Report Builder',
    body: 'Build a data report by adding columns and parameters. The builder has three zones: settings & activation on the left, the report canvas, and a live preview on the right.',
    placement: 'center',
  },
  {
    targetSelector: sel(REPORT_TOUR_TARGET.leftPanel),
    title: 'Activation & settings',
    body: 'Toggle which columns and sections appear on the report, and open the accordions for Parameters, Scheduled Delivery, Theme, and Visibility.',
    placement: 'right',
  },
  {
    targetSelector: sel(REPORT_TOUR_TARGET.surfaceTabs),
    title: 'Canvas surfaces',
    body: 'Switch between Columns & Layout, Filters, and Grouping surfaces — all on the same paged report sheet.',
    placement: 'bottom',
  },
  {
    targetSelector: sel(REPORT_TOUR_TARGET.addField),
    title: 'Add a field',
    body: 'Click here to open the field palette and add a column — or bind a declared parameter — at any position.',
    placement: 'bottom',
  },
  {
    targetSelector: sel(REPORT_TOUR_TARGET.columnCard),
    title: 'Column cards',
    body: 'Each column is a card on the canvas. Drag to reorder (the table reads top-to-bottom as left-to-right), or click a card to edit its properties on the right.',
    placement: 'left',
  },
  {
    targetSelector: sel(REPORT_TOUR_TARGET.columnMenu),
    title: 'Column menu',
    body: 'Every column has a menu for moving, duplicating, or removing it — and binding it to a parameter.',
    placement: 'left',
  },
  {
    targetSelector: sel(REPORT_TOUR_TARGET.parameters),
    title: 'Parameters',
    body: 'Declare runtime parameters (date ranges, lookups) that users supply at run time — and schedules bind at delivery time.',
    placement: 'right',
  },
  {
    targetSelector: sel(REPORT_TOUR_TARGET.schedule),
    title: 'Scheduled delivery',
    body: 'Schedule this report to be generated and emailed to recipients on a cadence.',
    placement: 'right',
  },
  {
    targetSelector: sel(REPORT_TOUR_TARGET.preview),
    title: 'Live preview',
    body: 'The live preview renders your report against local data as you build — the same engine that runs scheduled exports.',
    placement: 'left',
  },
  {
    targetSelector: sel(REPORT_TOUR_TARGET.savePublish),
    title: 'Save & Publish',
    body: 'Save keeps your draft; Publish makes the report available for runs and schedules.',
    placement: 'bottom',
  },
];
